import { Button } from "../../../components/Button";
import { TextInput } from "../../../components/TextInput";
import React, { useEffect, useState } from "react";
import { ReactComponent as AlertIcon } from "../../../assets/icons/Alert.svg";
import { Password } from "../components/Password";
import passwordValidation from "../register/Register";
import { ChangePassword } from "./ChangePassword";
import { ReactComponent as PraxisProIcon } from "../../../assets/icons/logo-white-bg.svg";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";
import { useApp } from "../../../services/app-context";

import { MFASetup } from "./MFASetup";
import { ShowGenericErrorToast, ShowToast } from "../../../services/toast";
import { getLoggerRef } from "../../../integrations/logger/logger-bridge";
import { setRefreshToken, setToken } from "../../../services/utils";
import {
  authApi,
  authErrorMessage,
  AuthResponse,
  BACKEND_AUTH_METHOD,
  CHALLENGE,
  establishBackendSession,
} from "../../../services/authApi";
import { isBackendAuthEnabled, isSSOEnabled } from "../../../integrations/posthog/featureFlags";
import { api } from "../../../services/api";
import { CohortForm } from "../CohortForm";
import { useNavigateToDashboard } from "../useNavigateToDashboard";
import { PRAXIS_PLAY_EVENTS } from "../../../integrations/posthog/events";
import { useAnalytics } from "../../../integrations/posthog/useAnalytics";

/**
 * Check if the background is white by checking if the url contains _white
 * @param url - The url of the background
 * @returns True if the background is white, false otherwise
 */
export const isWhiteLogo = (url: string) => {
  return url?.includes("_white");
}

export function Login() {
  const { captureEvent } = useAnalytics();
  const { tenantProfile, cognitoUserRef, fetchUserSession, preLoginLogos, setUserSession } = useApp();
  const backendAuthEnabled = isBackendAuthEnabled();
  // The in-flight backend challenge, if any. Cognito issues a single-use session
  // token per challenge, so this is replaced on every response, not accumulated.
  const [challenge, setChallenge] = useState<{
    name: string;
    session: string;
    username: string;
  } | null>(null);
  const [showCohortForm, setShowCohortForm] = useState(false);
  const [errors, setErrors] = React.useState({} as any);
  const [disabled, setDisabled] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string>("");
  const [showChangePassword, setShowChangePassword] = useState<any>(false);
  const [mfaSecret, setMfaSecret] = useState<any>();
  const [authCode, setAuthCode] = useState<string>("");
  const [showOtp, setShowOtp] = useState<{ isOpen: boolean; email: string }>({
    isOpen: false,
    email: "",
  });
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigateToDashboardWithCohortCheck = useNavigateToDashboard(
    async () => (await api.getMe())?.data
  );

  const navigateToDashboard = (shouldShowCohortForm = true, result?: any) => {
    localStorage.setItem("auth_method", backendAuthEnabled ? BACKEND_AUTH_METHOD : "native");
    const userId = result?.idToken?.payload["sub"];
    return navigateToDashboardWithCohortCheck(shouldShowCohortForm, userId, () =>
      setShowCohortForm(true)
    );
  }

  const handleCohortContinue = () => {
    setShowCohortForm(false);
    navigateToDashboard(false);
  }

  // Stands in for the Cognito onSuccess handlers below. fetchUserSession is
  // deliberately not called — it would look for Cognito SDK state that this
  // path never writes.
  const completeBackendLogin = async (idToken: string, email: string) => {
    const session = establishBackendSession(idToken, tenantProfile);
    setUserSession(session);
    setChallenge(null);
    setShowOtp({ isOpen: false, email: "" });
    setShowChangePassword(false);
    captureEvent(PRAXIS_PLAY_EVENTS.AUTH_SIGNIN_SUCCESS, {
      email,
      login_method: "password",
    });
    await navigateToDashboard(true, session);
  };

  /** Routes one /auth/login response: either it authenticated, or it challenged. */
  const handleAuthResponse = async (response: AuthResponse, email: string) => {
    if (response.idToken) {
      await completeBackendLogin(response.idToken, email);
      return;
    }
    if (!response.challengeName || !response.session) {
      setError("Something went wrong");
      return;
    }
    setChallenge({ name: response.challengeName, session: response.session, username: email });
    switch (response.challengeName) {
      case CHALLENGE.SOFTWARE_TOKEN_MFA:
        setShowOtp({ isOpen: true, email });
        break;
      case CHALLENGE.NEW_PASSWORD_REQUIRED:
        setShowChangePassword(true);
        break;
      default:
        // TODO: MFA_SETUP (first-time TOTP enrolment) needs associate/verify
        // software token endpoints the backend does not expose yet. Until it
        // does, these users have to sign in with the flag off.
        setError("This account needs a sign-in step we can't complete yet. Please contact support.");
    }
  };

  const handleBackendSubmit = async (email: string, password: string) => {
    try {
      await handleAuthResponse(await authApi.login(email, password), email);
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // The follow-up to an accepted challenge answer, kept apart from the call that
  // answered it: a failure here is not the answer being wrong, so it must not
  // say so and send the user back to re-enter something that was already right.
  const completeChallenge = async (response: AuthResponse, username: string) => {
    try {
      await handleAuthResponse(response, username);
    } catch {
      ShowGenericErrorToast({ supportLink: false });
    }
  };

  const verifyTotpViaBackend = async (authCode: string) => {
    if (!challenge) {
      return;
    }
    setVerificationLoading(true);
    let response: AuthResponse;
    try {
      response = await authApi.respondToChallenge(challenge.name, challenge.session, {
        USERNAME: challenge.username,
        SOFTWARE_TOKEN_MFA_CODE: authCode,
      });
    } catch (err) {
      // Cognito consumes the challenge session even on a rejected code, so
      // there is nothing left to retry against — send the user back to the form.
      setShowOtp({ isOpen: false, email: challenge.username });
      setChallenge(null);
      setAuthCode("");
      setLoading(false);
      ShowToast({
        message: authErrorMessage(err, "That code wasn't accepted. Please sign in again."),
        type: "error",
      });
      return;
    } finally {
      setVerificationLoading(false);
    }
    await completeChallenge(response, challenge.username);
  };

  const submitNewPasswordViaBackend = async (password: string) => {
    if (!challenge) {
      return;
    }
    setChangePasswordLoading(true);
    let response: AuthResponse;
    try {
      response = await authApi.respondToChallenge(challenge.name, challenge.session, {
        USERNAME: challenge.username,
        NEW_PASSWORD: password,
      });
    } catch (err) {
      // Modal left open, unlike the TOTP path: the common rejection here is a
      // policy violation or a reused password, which the user can fix in place.
      // The flat 401 can't be told apart from a spent session, so the message
      // covers that too rather than closing on the guess.
      ShowToast({
        message: authErrorMessage(
          err,
          "Couldn't set that password. Try a different one, or sign in again if this keeps happening."
        ),
        type: "error",
      });
      return;
    } finally {
      setChangePasswordLoading(false);
    }
    setShowChangePassword(false);
    await completeChallenge(response, challenge.username);
  };

  const verifySoftwareToken = async (authCode) => {
    if (authCode) {
      cognitoUserRef.current.verifySoftwareToken(authCode, "My TOTP device", {
        onSuccess: async (result) => {
          setVerificationLoading(false);
          if (result?.idToken?.jwtToken) {
            setToken(result?.idToken?.jwtToken);
            setRefreshToken(result?.refreshToken?.token);
            await navigateToDashboard(true, result);
            setShowOtp({ isOpen: false, email: "" });
            setMfaSecret(null);
          }
        },
        onFailure: (err) => {
          ShowToast({
            message: err.message || "Something went wrong",
            type: "error",
          });
          setVerificationLoading(false);
        },
      });
    }
  };

  const verifyTotp = (authCode) => {
    if (backendAuthEnabled) {
      void verifyTotpViaBackend(authCode);
      return;
    }
    if (authCode) {
      setVerificationLoading(true);
      cognitoUserRef.current.sendMFACode(
        authCode,
        {
          onSuccess: (result) => {
            setVerificationLoading(false);
            if (result?.idToken) {
              fetchUserSession();
              setToken(result?.idToken?.jwtToken);
              setRefreshToken(result?.refreshToken?.token);
              navigateToDashboard(true, result);
              setShowOtp({ isOpen: false, email: "" });
              setMfaSecret(null);
            }
          },
          onFailure: (err) => {
            if (err.code === "NotAuthorizedException") {
              setShowOtp({ isOpen: false, email: showOtp.email });
              setLoading(false);
            }
            ShowToast({
              message: err.message || "Something went wrong",
              type: "error",
            });
            setVerificationLoading(false);
          },
        },
        "SOFTWARE_TOKEN_MFA"
      );
    }
  };

  const handleSetupMFA = (email: string) => {
    if (cognitoUserRef.current.associateSoftwareToken) {
      cognitoUserRef.current.associateSoftwareToken({
        onFailure: (error) => {
          console.error(error);
        },
        associateSecretCode: (code) => {
          setShowOtp({ isOpen: true, email: email });
          setMfaSecret({ code, email });
        },
      });
    }
  };

  const handlePasswordChange =
    (userAttributes: any) =>
      ({ password }: any) => {
        if (backendAuthEnabled) {
          void submitNewPasswordViaBackend(password);
          return;
        }
        const { email, ...rest } = userAttributes;
        const payload = {
          ...rest,
        };
        setChangePasswordLoading(true);
        cognitoUserRef.current.completeNewPasswordChallenge(
          password,
          {
            family_name: rest.family_name,
            given_name: rest.given_name,
          },
          {
            onSuccess: (result) => {
              if (result?.idToken) {
                fetchUserSession();
                setToken(result?.idToken?.jwtToken);
                setRefreshToken(result?.refreshToken?.token);
                navigateToDashboard(true, result);
                setShowChangePassword(false);
                setMfaSecret(null);
              } else {
                setError("Something went wrong");
              }
              setChangePasswordLoading(false);
            },
            onFailure: (err) => {
              ShowToast({
                message: err.message || "Something went wrong",
                type: "error",
              });
              setChangePasswordLoading(false);
            },
            totpRequired: () => {
              setShowOtp({ isOpen: true, email: email });
              setChangePasswordLoading(false);
            },
            mfaSetup: function () {
              cognitoUserRef.current.associateSoftwareToken({
                onFailure: (error) => {
                  setChangePasswordLoading(false);
                },
                associateSecretCode: (code) => {
                  setShowChangePassword(false);
                  setChangePasswordLoading(false);
                  setShowOtp({ isOpen: true, email });
                  setMfaSecret({ code, email });
                },
              });
            },
          }
        );
      };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // get the form data
    const formData = new FormData(e.currentTarget);
    // convert the form data to an object
    const data = Object.fromEntries(formData);
    // log the results
    setError("");
    setLoading(true);
    try {
      captureEvent(PRAXIS_PLAY_EVENTS.AUTH_SIGNIN_INITIATED, {
        email: data.email,
        login_method: "password",
      });

      if (backendAuthEnabled) {
        await handleBackendSubmit(data.email as string, data.password as string);
        return;
      }

      var poolData = {
        UserPoolId: tenantProfile.user_pool_id, // Your user pool id here
        ClientId: tenantProfile.user_pool_client_id, // Your client id here
      };
      var userPool = new CognitoUserPool(poolData);

      const authenticationData = {
        Username: data.email as string,
        Password: data.password as string,
      };
      const authenticationDetails = new AuthenticationDetails(
        authenticationData
      );

      const userData = {
        Username: data.email as string,
        Pool: userPool,
      };
      cognitoUserRef.current = new CognitoUser(userData);

      cognitoUserRef.current.authenticateUser(authenticationDetails, {
        onSuccess: function (result) {
          if (result?.idToken) {

            captureEvent(PRAXIS_PLAY_EVENTS.AUTH_SIGNIN_SUCCESS, {
              email: data.email,
              login_method: "password",
              session_duration_ms: result?.signInDetails?.AuthenticationResult?.ExpiresIn,
            });
            fetchUserSession();
            setToken(result?.idToken?.jwtToken);
            setRefreshToken(result?.refreshToken?.token);
            navigateToDashboard(true, result);
            setShowOtp({ isOpen: false, email: "" });
            setMfaSecret(null);
          } else {
            setError("Something went wrong");
          }
        },

        onFailure: function (err) {
          setLoading(false);
          setError(err.message);
        },
        totpRequired: () => {
          setShowOtp({ isOpen: true, email: data.email as string });
        },
        mfaSetup: function (challengeName, challengeParameters) {
          cognitoUserRef.current.associateSoftwareToken({
            onFailure: (error) => {
              console.error(error);
            },
            associateSecretCode: (code) => {
              setShowOtp({ isOpen: true, email: data.email as string });
              setMfaSecret({ code, email: data.email });
            },
          });
        },
        newPasswordRequired: function (userAttributes, requiredAttributes) {
          // User was signed up by an admin and must provide new
          // password and required attributes, if any, to complete
          // authentication.

          // the api doesn't accept this field back
          setLoading(false);
          delete userAttributes.email_verified;
          const { email, ...rest } = userAttributes;
          setShowChangePassword(userAttributes);
        },
      });
    } catch (error: any) {
      setLoading(false);
      if (error?.response?.status === 401) {
        setError("Invalid email or password");
        return;
      }
      setError("Something went wrong");
      return false;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLFormElement>) => {
    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData);
    if (
      e.target.name === "password" &&
      !passwordValidation(data.password.toString())
    ) {
      setErrors((prev) => ({
        ...prev,
        password: "Invalid password",
      }));
    } else {
      setErrors((prev) => ({ ...prev, password: "" }));
    }
    if (
      data.email &&
      data.password &&
      passwordValidation(data.password.toString())
    ) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  };

  const handleChangePasswordClose = (success?: boolean) => {
    setShowChangePassword(false);
    if (success) {
      navigateToDashboard();
    }
  };

  useEffect(() => {
    if (tenantProfile) {
      var poolData = {
        UserPoolId: tenantProfile.user_pool_id, // Your user pool id here
        ClientId: tenantProfile.user_pool_client_id, // Your client id here
      };
      var userPool = new CognitoUserPool(poolData);
      var cognitoUser = userPool.getCurrentUser();
      if (cognitoUser != null) {
        cognitoUser.getSession(function (err, session) {
          if (err) {
            // A stale stored session is not actionable here (the user is already on the
            // login page and just signs in again), so log only.
            getLoggerRef()?.warn("Login page: stored Cognito session check failed", {
              errorMessage: err.message,
            });
            return;
          }

          // Instantiate aws sdk service objects now that the credentials have been updated.
          // example: var s3 = new AWS.S3();
        });
      }
    }
  }, [tenantProfile]);

  const handleSSOLogin = () => {
    setIsLoading(true);
    if (tenantProfile?.sso_redirect_url) {
      window.location.href = tenantProfile?.sso_redirect_url;
    }
  };

  return (
    <div
      className="h-screen flex flex-col"
      style={{ background: "linear-gradient(180deg, var(--loginBgGradient1) 0%, var(--loginBgGradient2) 52%, var(--loginBgGradient3) 100%)" }}
    >
      <div className="flex-1 min-h-0 flex items-center justify-center overflow-auto">
        <div className="lg:w-[700px] flex flex-col gap-4 items-center justify-center">
          {preLoginLogos?.["logo"] ?
            <img
              src={preLoginLogos?.["logo"]}
              alt="logo"
              className="w-[500px] max-h-[150px] object-contain"
            /> :
            <PraxisProIcon />
          }
          <div className="text-center text-white text-base leading-6">Let's get started!</div>
          <div className="bg-white rounded-3xl p-8 w-full">
            <div>
              {error && (
                <div className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1">
                  <AlertIcon /> {error}
                </div>
              )}
              {showCohortForm &&
                <>
                  <div className="mt-3 justify-start text-text-primary text-xl font-normal">Select your cohort to continue</div>
                  <div className="border-b border-[#DDD] w-full my-4"></div>
                </>}
              {showCohortForm ? <CohortForm onContinue={() => handleCohortContinue()} /> :
                <>
                  <form
                    onSubmit={handleSubmit}
                    onChange={handleChange}
                  >
                    <div className="mb-8 flex flex-col gap-1.5">
                      <label htmlFor="email" className="text-sm text-text-primary/80 font-semibold">
                        Email
                      </label>
                      <TextInput
                        type="email"
                        name="email"
                        placeholder="Enter your email address"
                      />
                    </div>
                    <div className=" flex flex-col gap-1.5">
                      <label htmlFor="password" className="text-sm text-text-primary/80 font-semibold">
                        Password
                      </label>
                      <Password
                        type="password"
                        name="password"
                        placeholder="Enter a password"
                      />
                      {errors.password && (
                        <div className="text-red-500 text-xs font-medium">
                          {errors.password}
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end mt-[14px]">
                      <a
                        href="/forgot-password"
                        className="text-text-primary/80 text-sm font-medium"
                      >
                        Forgot password?
                      </a>
                    </div>

                    <div className="mt-7">
                      <Button
                        type="submit"
                        variant="primary"
                        className="!rounded-full w-full"
                        disabled={disabled || loading}
                        loading={loading}
                      >
                        Sign In
                      </Button>
                    </div>
                  </form>
                  {isSSOEnabled() && tenantProfile?.sso_redirect_url && (
                    <div className="mt-7">
                      <Button
                        type="button"
                        variant="primary"
                        className="!rounded-full w-full"
                        disabled={isLoading}
                        loading={isLoading}
                        onClick={handleSSOLogin}
                      >
                        {isLoading ? 'Signing in...' : 'Sign in with SSO'}
                      </Button>
                    </div>
                  )}
                </>
              }
            </div>
          </div>
        </div>
      </div>
      <div className="shrink-0 text-center text-white/[0.81] text-[13px] font-semibold pb-7">Powered by PraxisPro</div>
      {!!showChangePassword && (
        <ChangePassword
          isOpen={!!showChangePassword}
          onClose={handleChangePasswordClose}
          onSave={handlePasswordChange(showChangePassword)}
          loading={changePasswordLoading}
        // user={showChangePassword}
        // fromLogin={true}
        />
      )}
      {showOtp?.isOpen && (
        <MFASetup
          isOpen={showOtp.isOpen}
          loading={verificationLoading}
          onClose={() => setShowOtp({ isOpen: false, email: "" })}
          authCode={authCode}
          setAuthCode={setAuthCode}
          verifyTotp={mfaSecret?.code ? verifySoftwareToken : verifyTotp}
          mfaSecret={mfaSecret}
          onSetupMFA={() => handleSetupMFA(showOtp.email)}
          tenantProfile={tenantProfile}
        />
      )}
    </div>
  );
}
